import Typography from "@portal/components/atoms/v2/typography";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { useRouter } from "next/navigation";
import { MainBox } from "../components/mainBox";
import { AgreementSummary, SuitabilityReport } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { MapFundsToStartUpAgreement } from "../completesaving/helpers/GetStartUpAgreementRequest";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import Button from "@portal/components/atoms/v2/buttons/button";
import { OnFooterClick } from "./handlers/OnFooterClick";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import Link from "next/link";
import { GetEndDateForStartUpAgreement } from "@portal/helpers";
import { format } from "date-fns";
import { useEffect } from "react";

interface IFinalPageProps extends IPageProps {
  avtaleGiroStatus?: string;
}

export const FinalPage = ({ centerId, isDefaultLanguage, avtaleGiroStatus }: IFinalPageProps) => {
  const { request, response, suitabilityReport } = useAppSelector(state => state.persisted.savingsAgreement);
  const { data: customerInfo } = useCustomerInfo();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const data = request.agreementData;
  const savingCalculationsRequest = useAppSelector(state => state.persisted.savingCalculations.request);

  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const cashAccount = request?.agreementData?.cashAccount ?? "";
  const portfolioId = request.portfolioId ?? "";
  const userName = `${customerInfo?.firstname} ${customerInfo?.lastName}`;

  const oneTimeDeposit = savingCalculationsRequest.oneTimeDeposit;
  const numOfMonthsStartupAmount = savingCalculationsRequest.numOfMonthsStartupAmount;
  const hasStartUpAgreement = oneTimeDeposit !== 0;

  useEffect(() => {
    if (avtaleGiroStatus === "ok") {
      fetch(`/api/missing-payment-agreements/${response.agreement?.savingsAgreementId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  }, [avtaleGiroStatus, response.agreement?.savingsAgreementId]);

  return (
    <>
      <MainBox>
        <div className="px-2">
          <Typography variant="H6">{texts.PAGE_18_SUBHEADER}</Typography>
          <TextLine />
          <Typography variant="Body">{texts.PAGE_18_INGRESS}</Typography>
          <Typography variant="BodySpan">{texts.PAGE_18_INGRESS_2_A}</Typography>
          <Link href="/" className="text-blueHighlighted">
            {texts.PAGE_18_INGRESS_2_LINK_TEXT}
          </Link>
          <Typography variant="BodySpan">{texts.PAGE_18_INGRESS_2_B}</Typography>
        </div>
        <div className={`flex flex-col ${hasStartUpAgreement && `lg:flex-row`}`}>
          {hasStartUpAgreement && (
            <AgreementSummary
              cashAccount={cashAccount}
              dateTo={format(new Date(GetEndDateForStartUpAgreement(numOfMonthsStartupAmount)), "dd.MM.yyyy")}
              portfolioId={portfolioId}
              isDefaultLanguage={isDefaultLanguage}
              userName={userName}
              header={numOfMonthsStartupAmount === 1 ? texts.PAGE_17_START_HEADER_ONE_TIME : texts.PAGE_17_START_HEADER}
              funds={MapFundsToStartUpAgreement(data.funds, oneTimeDeposit, numOfMonthsStartupAmount)}
              startUpAmount={oneTimeDeposit}
              startUpAgreement
              oneTime={numOfMonthsStartupAmount === 1}
            />
          )}
          <AgreementSummary
            cashAccount={cashAccount}
            dateTo={texts.PAGE_17_NO_DATE}
            portfolioId={portfolioId}
            isDefaultLanguage={isDefaultLanguage}
            userName={userName}
            header={data.adjustmentComment ?? ""}
            funds={data.funds}
          />
        </div>
        {suitabilityReport && (
          <SuitabilityReport suitabilityReport={suitabilityReport} header={texts.PAGE_17_SUITABILITY_HEADER} />
        )}

        {avtaleGiroStatus && avtaleGiroStatus.toLocaleLowerCase() !== "ok" && (
          <div className="mt-2">
            <InfoBoxComponent
              texts={[
                `${texts.PAGE_18_AVTALE_GIRO_ERROR.replace("$KID", response.agreement?.kid ?? "").replace(
                  "$KONTONUMMER",
                  "3626 30 29539",
                )}`,
              ]}
            />
          </div>
        )}
      </MainBox>

      <div className="flex w-full p-5 justify-between">
        <Button onClick={() => OnFooterClick(centerId, dispatch, router)} text={texts.TEXT_CLOSE} />
        <Button onClick={() => OnFooterClick(centerId, dispatch, router, true)} text={texts.BUTTON_FINISH} wide primary />
      </div>
    </>
  );
};
